// Copyright 2025, University of Maryland, All Rights Reserved
import { apiClient } from './api-client'
import type { 
    DatasourceBill, 
    DatasourceBillsResponse, 
    ListDatasourceBillsParams 
} from '@/types/datasource-bill'
import { useMutation } from '@tanstack/react-query'


export const datasourceBillsApi = {
    listDatasourceBills: async ({
        data_source,
        session_code,
        limit = 100,
        next_token,
        start_key,
        search_query,
        sort_field,
        sort_direction
    }: ListDatasourceBillsParams): Promise<DatasourceBillsResponse> => {
        const params = new URLSearchParams()
        
        if (limit) params.append('limit', limit.toString())
        if (next_token) params.append('next_token', next_token)
        if (start_key) params.append('start_key', start_key)
        if (search_query) params.append('search_query', search_query)
        if (sort_field) params.append('sort_field', sort_field)
        if (sort_direction) params.append('sort_direction', sort_direction)

        try {
            const requestUrl = `/datasource/${encodeURIComponent(data_source)}/bills/${encodeURIComponent(session_code)}?${params.toString()}`
            console.log('[datasourceBillsApi] Making request to:', requestUrl)

            const response = await apiClient.get<DatasourceBillsResponse>(requestUrl)

            console.log('[datasourceBillsApi] Response status:', response.status)
            console.log('[datasourceBillsApi] Raw response data:', response.data)
            console.log('[datasourceBillsApi] Response:', response.data.items.length, 'items')
            return response.data
        } catch (error) {
            console.error('Failed to fetch datasource bills:', error instanceof Error ? error.message : String(error))
            throw error
        }
    },

    loadAllDatasourceBills: async ({
        data_source,
        session_code,
        search_query,
        sort_field,
        sort_direction,
        on_progress
    }: Omit<ListDatasourceBillsParams, 'limit' | 'next_token' | 'start_key'> & {
        on_progress?: (currentCount: number, batch: DatasourceBill[]) => void
    }): Promise<DatasourceBill[]> => {
        console.log('[datasourceBillsApi] Starting to load all bills for:', { data_source, session_code })

        const allBills: DatasourceBill[] = []
        let nextToken: string | undefined = undefined
        let currentCount = 0
        let pageCount = 0

        try {
            do {
                pageCount++
                console.log(`[datasourceBillsApi] Loading page ${pageCount}${nextToken ? ` (token: ${nextToken.substring(0, 20)}...)` : ''}`)

                const response = await datasourceBillsApi.listDatasourceBills({
                    data_source,
                    session_code,
                    limit: 100, // Use a larger batch size for loading all
                    next_token: nextToken,
                    search_query,
                    sort_field,
                    sort_direction
                })

                if (response.items.length > 0) {
                    allBills.push(...response.items)
                    currentCount += response.items.length
                    console.log(`[datasourceBillsApi] Page ${pageCount}: +${response.items.length} bills (total: ${currentCount})`)

                    // Call progress callback if provided
                    on_progress?.(currentCount, response.items)
                } else {
                    console.log(`[datasourceBillsApi] Page ${pageCount}: No bills returned`)
                }

                nextToken = response.next_token

                // Safety check to prevent infinite loops
                if (pageCount > 1000) {
                    console.warn('[datasourceBillsApi] Reached maximum page limit (1000), stopping')
                    break
                }

            } while (nextToken)

            console.log(`[datasourceBillsApi] Completed loading all bills: ${allBills.length} total bills in ${pageCount} pages`)
            return allBills

        } catch (error: any) {
            console.error('[datasourceBillsApi] Failed to load all bills:', error.message)
            throw error
        }
    },

    getFileContent: async (s3_key: string): Promise<string> => {
        try {
            const response = await apiClient.get<string>(`/bills/content/${s3_key}`, {
                responseType: 'text'
            })
            return response.data
        } catch (error) {
            console.error('Failed to get file content:', error)
            throw error
        }
    },

    downloadFile: async (s3_key: string, filename?: string): Promise<void> => {
        try {
            const response = await apiClient.get<Blob>(`/bills/content/${s3_key}`, {
                responseType: 'blob'
            })
            
            // Create a blob URL and trigger download
            const blob = new Blob([response.data])
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename || `file_${s3_key}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Failed to download file:', error)
            throw error
        }
    },

    getFileContentUrl: (s3_key: string): string => {
        // Get the base URL from the API configuration
        const baseUrl = import.meta.env.VITE_API_URL || ''
        return `${baseUrl}/bills/content/${s3_key}`
    }
}

export const useGetDatasourceFileContent = () => {
    return useMutation({
        mutationFn: (s3_key: string) => datasourceBillsApi.getFileContent(s3_key)
    })
}

export const useDownloadFile = () => {
    return useMutation({
        mutationFn: ({ s3_key, filename }: { s3_key: string; filename?: string }) => 
            datasourceBillsApi.downloadFile(s3_key, filename)
    })
}
